import React, { useEffect, useState } from 'react';
import SlideOver from '../../shared/components/SlideOver';
import AddressAutocomplete from '../../shared/components/AddressAutocomplete';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
  country: '',
  lat: null,
  lng: null,
  notes: '',
};

// Client form — address autocomplete (Photon via the /api/geocode proxy)
// populates the STRUCTURED address fields below (not a free-text string),
// including lat/lng for the jobs map. Fields stay manually editable.
export default function ClientForm({ open, onClose, client, onSaved }) {
  const { add, update } = useData();
  const { notify } = useToast();
  const [form, setForm] = useState(client || EMPTY);

  // Reset on every open — this instance stays mounted between open/close
  // cycles (create from ClientsList, edit from ClientDetail), so the
  // useState initializer alone would leak the previous session's values.
  useEffect(() => {
    if (open) setForm(client || EMPTY);
  }, [open, client]);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const applyPlace = (place) => setForm((current) => ({
    ...current,
    address_line1: place.line1 || current.address_line1,
    address_line2: place.line2 || '',
    city: place.city || '',
    postcode: place.postcode || '',
    country: place.country || '',
    lat: place.lat ?? current.lat,
    lng: place.lng ?? current.lng,
  }));

  async function save(event) {
    event.preventDefault();
    try {
      const value = {
        ...form,
        initials: form.name.split(' ').map((word) => word[0]).join('').slice(0, 2).toUpperCase(),
        status: client?.status || 'new',
      };
      if (client) {
        await update('clients', client.id, value);
      } else {
        await add('clients', { ...value, id: `client_${Date.now()}` });
      }
      onClose();
      onSaved?.();
    } catch (err) {
      notify(err.message || 'Failed to save client', 'error');
    }
  }

  const initialAddress = client
    ? [client.address_line1, client.address_line2, client.city, client.postcode].filter(Boolean).join(', ')
    : '';

  return (
    <SlideOver open={open} title={client ? 'Edit client' : 'New client'} onClose={onClose}>
      <form className="drawer-form" onSubmit={save}>
        <label>Name<input required value={form.name || ''} onChange={(event) => change('name', event.target.value)} /></label>
        <label>Email<input type="email" value={form.email || ''} onChange={(event) => change('email', event.target.value)} /></label>
        <label>Phone<input value={form.phone || ''} onChange={(event) => change('phone', event.target.value)} /></label>

        <div className="form-section">
          <strong>Address</strong>
          <AddressAutocomplete onSelect={applyPlace} initial={initialAddress} />
          <label>Line 1<input value={form.address_line1 || ''} onChange={(event) => change('address_line1', event.target.value)} /></label>
          <label>Line 2<input value={form.address_line2 || ''} onChange={(event) => change('address_line2', event.target.value)} /></label>
          <div className="form-row">
            <label>Town / city<input value={form.city || ''} onChange={(event) => change('city', event.target.value)} /></label>
            <label>Postcode<input value={form.postcode || ''} onChange={(event) => change('postcode', event.target.value)} /></label>
          </div>
          <label>Country<input value={form.country || ''} onChange={(event) => change('country', event.target.value)} /></label>
        </div>

        <label>Internal notes<textarea rows="4" value={form.notes || ''} onChange={(event) => change('notes', event.target.value)} /></label>
        <div className="drawer-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button className="primary-button">Save client</button>
        </div>
      </form>
    </SlideOver>
  );
}
